/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import { serviceRequestsAPI, contractorsAPI } from '../../services/api';
import { symbolFor } from '../../utils/currencyTotals';
import { useAppData } from '../../context/AppDataContext';
import { useBilling } from '../billing/BillingContext';
import { LockedPage } from '../billing/UpgradePrompts';

const CATEGORIES = ['General Maintenance', 'Plumbing', 'Electrical', 'Gas', 'Roofing', 'Cleaning', 'Landscaping', 'Carpentry', 'Plastering', 'Painting & Decorating', 'Flooring', 'HVAC', 'Pest Control', 'Safety & Compliance', 'Other'];
const PRIORITIES = ['low', 'medium', 'high', 'emergency'];
const STATUSES   = ['new', 'assigned', 'quote_received', 'in_progress', 'awaiting_parts', 'completed', 'closed', 'cancelled'];

const PRIORITY_COLOURS = {
  low:       { bg: '#f0fdf4', text: '#166534', label: 'Low' },
  medium:    { bg: '#eff6ff', text: '#1e40af', label: 'Medium' },
  high:      { bg: '#fff7ed', text: '#9a3412', label: 'High' },
  emergency: { bg: '#fef2f2', text: '#991b1b', label: 'Emergency' },
};

const STATUS_COLOURS = {
  new:            { bg: '#f1f5f9', text: '#475569', label: 'New' },
  assigned:       { bg: '#eff6ff', text: '#1e40af', label: 'Assigned' },
  quote_received: { bg: '#fef3c7', text: '#92400e', label: 'Quote Received' },
  in_progress:    { bg: '#ecfdf5', text: '#065f46', label: 'In Progress' },
  awaiting_parts: { bg: '#fff7ed', text: '#9a3412', label: 'Awaiting Parts' },
  completed:      { bg: '#f0fdf4', text: '#166534', label: 'Completed' },
  closed:         { bg: '#f8fafc', text: '#64748b', label: 'Closed' },
  cancelled:      { bg: '#fef2f2', text: '#991b1b', label: 'Cancelled' },
};

const fmtDate = (d) => {
  if (!d) return 'not set';
  const s = typeof d === 'string' ? d.substring(0, 10) : new Date(d).toISOString().substring(0, 10);
  const [y, m, day] = s.split('-');
  return `${day}/${m}/${y}`;
};

const Badge = ({ value, map }) => {
  const cfg = map[value] || { bg: '#f1f5f9', text: '#475569', label: value };
  return (
    <span style={{ background: cfg.bg, color: cfg.text, fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.5rem', borderRadius: '0.25rem', textTransform: 'capitalize' }}>
      {cfg.label}
    </span>
  );
};

const defaultForm = {
  property_id: '', room_reference: '', title: '', description: '',
  category: 'General Maintenance', priority: 'medium',
  reported_date: new Date().toISOString().substring(0, 10), notes: '',
  contractor_id: '',
};

const TIMELINE = [
  { status: 'new',            label: 'Raised' },
  { status: 'assigned',       label: 'Assigned' },
  { status: 'quote_received', label: 'Quoted' },
  { status: 'in_progress',    label: 'In Progress' },
  { status: 'completed',      label: 'Completed' },
];

const timelineIndex = (status) => {
  const idx = TIMELINE.findIndex(s => s.status === status);
  return idx === -1 ? (status === 'awaiting_parts' ? 3 : 0) : idx;
};

export default function ServiceRequests() {
  const { canAccess } = useBilling();
  if (!canAccess('service')) return <LockedPage feature="service" />;
  const { properties, setExpenses } = useAppData();
  const [requests, setRequests]           = useState([]);
  const [contractors, setContractors]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selected, setSelected]           = useState(null);
  const [showAdd, setShowAdd]             = useState(false);
  const [form, setForm]                   = useState(defaultForm);
  const [saving, setSaving]               = useState(false);
  const [formError, setFormError]         = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterProp, setFilterProp]       = useState('');
  const [filterPriority, setFilterPri]    = useState('');
  const [search, setSearch]               = useState('');
  const [assignId, setAssignId]           = useState('');
  const [completeForm, setCompleteForm]   = useState({ actual_cost: '', notes: '', create_expense: true });
  const [rejectComment, setRejectComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState('');

  const [activeMainTab, setActiveMainTab] = useState('active'); // active | completed | cancelled

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [reqs, cons] = await Promise.all([
        serviceRequestsAPI.getAll(),
        contractorsAPI.getAll(),
      ]);
      setRequests(reqs);
      setContractors(cons.filter(c => c.active));
    } catch (e) { console.error('Failed to load:', e); }
    finally { setLoading(false); }
  };

  const reload = async () => {
    const reqs = await serviceRequestsAPI.getAll();
    setRequests(reqs);
    if (selected) {
      const updated = reqs.find(r => r.id === selected.id);
      if (updated) setSelected(updated);
    }
  };

  const selectedProperty = useMemo(() =>
    properties.find(p => String(p.id) === String(form.property_id)),
    [properties, form.property_id]
  );
  const isHMO = (selectedProperty?.property_type || selectedProperty?.propertyType || '') === 'hmo';

  const stats = useMemo(() => ({
    open:      requests.filter(r => ['new', 'assigned'].includes(r.status)).length,
    quotes:    requests.filter(r => r.status === 'quote_received').length,
    active:    requests.filter(r => ['in_progress', 'awaiting_parts'].includes(r.status)).length,
    completed: requests.filter(r => ['completed', 'closed'].includes(r.status)).length,
    cancelled: requests.filter(r => r.status === 'cancelled').length,
  }), [requests]);

  const filtered = useMemo(() => {
    let list = requests;
    // Main tab filter
    if (activeMainTab === 'active') {
      list = list.filter(r => !['completed', 'closed', 'cancelled'].includes(r.status));
    } else if (activeMainTab === 'completed') {
      list = list.filter(r => ['completed', 'closed'].includes(r.status));
    } else if (activeMainTab === 'cancelled') {
      list = list.filter(r => r.status === 'cancelled');
    }
    if (filterStatus)   list = list.filter(r => r.status === filterStatus);
    if (filterProp)     list = list.filter(r => String(r.property_id) === filterProp);
    if (filterPriority) list = list.filter(r => r.priority === filterPriority);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.title.toLowerCase().includes(q) ||
        (r.property_address || '').toLowerCase().includes(q) ||
        (r.contractor_name || '').toLowerCase().includes(q)
      );
    }
    const priOrder = { emergency: 0, high: 1, medium: 2, low: 3 };
    return list.sort((a, b) => (priOrder[a.priority] ?? 2) - (priOrder[b.priority] ?? 2));
  }, [requests, activeMainTab, filterStatus, filterProp, filterPriority, search]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.property_id) { setFormError('Please select a property'); return; }
    if (!form.title.trim()) { setFormError('Title is required'); return; }
    setSaving(true);
    try {
      await serviceRequestsAPI.create(form);
      await reload();
      setShowAdd(false);
      setForm(defaultForm);
    } catch (e) { setFormError('Failed to create service request'); }
    finally { setSaving(false); }
  };

  const handleAssign = async () => {
    if (!assignId) { setActionError('Please select a contractor'); return; }
    setActionLoading(true); setActionError('');
    try {
      await serviceRequestsAPI.assign(selected.id, parseInt(assignId));
      await reload(); setAssignId('');
    } catch (e) { setActionError('Failed to assign contractor'); }
    finally { setActionLoading(false); }
  };

  const handleAcceptQuote = async () => {
    setActionLoading(true); setActionError('');
    try { await serviceRequestsAPI.acceptQuote(selected.id); await reload(); }
    catch (e) { setActionError('Failed to accept quote'); }
    finally { setActionLoading(false); }
  };

  const handleRejectQuote = async () => {
    if (!window.confirm('Reject this quote? The contractor will be notified.')) return;
    setActionLoading(true); setActionError('');
    try {
      await serviceRequestsAPI.rejectQuote(selected.id, rejectComment);
      await reload();
      setRejectComment('');
    } catch (e) { setActionError('Failed to reject quote'); }
    finally { setActionLoading(false); }
  };

  const handleComplete = async () => {
    setActionLoading(true); setActionError('');
    try {
      const result = await serviceRequestsAPI.complete(
        selected.id,
        completeForm.actual_cost ? parseFloat(completeForm.actual_cost) : null,
        completeForm.notes,
        completeForm.actual_cost ? completeForm.create_expense : false
      );
      // If expense was auto-created, add it to global expenses state
      if (result?.expenseCreated) {
        setExpenses(prev => [result.expenseCreated, ...prev]);
      }
      await reload();
      setCompleteForm({ actual_cost: '', notes: '', create_expense: true });
      setSelected(null);
      setActiveMainTab('completed');
    } catch (e) { setActionError('Failed to mark complete'); }
    finally { setActionLoading(false); }
  };

  const handleUpdateStatus = async (status) => {
    setActionLoading(true); setActionError('');
    try {
      const result = await serviceRequestsAPI.update(selected.id, { ...selected, status });
      setRequests(prev => prev.map(r => r.id === result.id ? result : r));
      setSelected(result);
    } catch (e) { setActionError('Failed to update status'); }
    finally { setActionLoading(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this service request? It will be moved to the Cancelled tab.')) return;
    try {
      await serviceRequestsAPI.cancel(selected.id);
      await reload();
      setSelected(null);
      setActiveMainTab('cancelled');
    } catch (e) { setActionError('Failed to cancel'); }
  };

  const sym = selected ? (selected.currency === 'EUR' ? 'EUR' : 'GBP') : 'GBP';
  const symChar = symbolFor(selected && selected.currency);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Service Requests</h1>
          <p className="page-subtitle">{stats.open + stats.quotes + stats.active} active requests</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowAdd(true); setFormError(''); setForm(defaultForm); }}>+ New Request</button>
      </div>

      {/* Main tabs */}
      <div className="tabs" style={{ marginBottom: '1.5rem' }}>
        <button className={`tab-btn ${activeMainTab === 'active' ? 'active' : ''}`}
          onClick={() => { setActiveMainTab('active'); setSelected(null); setFilterStatus(''); }}>
          🔧 Active {stats.open + stats.quotes + stats.active > 0 && <span style={{ marginLeft: '0.35rem', background: 'var(--danger)', color: '#fff', borderRadius: '0.75rem', padding: '0.1rem 0.45rem', fontSize: '0.7rem', fontWeight: 700 }}>{stats.open + stats.quotes + stats.active}</span>}
        </button>
        <button className={`tab-btn ${activeMainTab === 'completed' ? 'active' : ''}`}
          onClick={() => { setActiveMainTab('completed'); setSelected(null); setFilterStatus(''); }}>
          ✅ Completed {stats.completed > 0 && <span style={{ marginLeft: '0.35rem', background: 'var(--success)', color: '#fff', borderRadius: '0.75rem', padding: '0.1rem 0.45rem', fontSize: '0.7rem', fontWeight: 700 }}>{stats.completed}</span>}
        </button>
        <button className={`tab-btn ${activeMainTab === 'cancelled' ? 'active' : ''}`}
          onClick={() => { setActiveMainTab('cancelled'); setSelected(null); setFilterStatus(''); }}>
          ❌ Cancelled {stats.cancelled > 0 && <span style={{ marginLeft: '0.35rem', background: '#6b7280', color: '#fff', borderRadius: '0.75rem', padding: '0.1rem 0.45rem', fontSize: '0.7rem', fontWeight: 700 }}>{stats.cancelled}</span>}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Open',          value: stats.open,      color: '#1e40af', status: 'new' },
          { label: 'Quote Pending', value: stats.quotes,    color: '#92400e', status: 'quote_received' },
          { label: 'In Progress',   value: stats.active,    color: '#065f46', status: 'in_progress' },
          { label: 'Completed',     value: stats.completed, color: '#166534', status: 'completed' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '1rem', textAlign: 'center', cursor: 'pointer' }}
            onClick={() => setFilterStatus(filterStatus === s.status ? '' : s.status)}>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="form-control" style={{ maxWidth: '220px' }} placeholder="Search..."
            value={search} onChange={e => setSearch(e.target.value)} />
          <select className="form-control" style={{ maxWidth: '160px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_COLOURS[s]?.label || s}</option>)}
          </select>
          <select className="form-control" style={{ maxWidth: '200px' }} value={filterProp} onChange={e => setFilterProp(e.target.value)}>
            <option value="">All properties</option>
            {properties.filter(p => !p.archived).map(p => <option key={p.id} value={String(p.id)}>{p.address}</option>)}
          </select>
          <select className="form-control" style={{ maxWidth: '140px' }} value={filterPriority} onChange={e => setFilterPri(e.target.value)}>
            <option value="">All priorities</option>
            {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_COLOURS[p]?.label || p}</option>)}
          </select>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            {filtered.length} request{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* List + detail panel */}
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 400px' : '1fr', gap: '1.5rem', alignItems: 'start' }}>

        {/* List */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <span>{activeMainTab === 'completed' ? '✅' : activeMainTab === 'cancelled' ? '❌' : '🔧'}</span>
                <h3>{activeMainTab === 'completed' ? 'No completed requests' : activeMainTab === 'cancelled' ? 'No cancelled requests' : 'No active requests'}</h3>
                <p>{activeMainTab === 'active' ? 'Raise a maintenance request to get started' : `No ${activeMainTab} requests to show`}</p>
                {activeMainTab === 'active' && <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ New Request</button>}
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {filtered.map(r => {
                const priCfg = PRIORITY_COLOURS[r.priority] || PRIORITY_COLOURS.medium;
                const isSelected = selected?.id === r.id;
                return (
                  <div key={r.id} className="card" style={{ cursor: 'pointer', borderLeft: `4px solid ${priCfg.text}`, background: isSelected ? 'var(--bg-light)' : undefined }}
                    onClick={() => { setSelected(r); setActionError(''); setAssignId(''); setCompleteForm({ actual_cost: '', notes: '', create_expense: true }); setRejectComment(''); }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.25rem' }}>{r.title}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                          🏠 {r.property_address}
                          {r.room_reference ? ` · Room: ${r.room_reference}` : ''}
                          {r.contractor_name ? ` · 🔧 ${r.contractor_name}` : ''}
                        </div>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <Badge value={r.status} map={STATUS_COLOURS} />
                          <Badge value={r.priority} map={PRIORITY_COLOURS} />
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>{r.category}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        <div>{fmtDate(r.reported_date)}</div>
                        {r.quote_amount && <div style={{ fontWeight: 700, color: '#166534', marginTop: '0.25rem' }}>{symbolFor(r.currency)}{parseFloat(r.quote_amount).toFixed(2)}</div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="card" style={{ position: 'sticky', top: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{selected.title}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>{selected.property_address}</div>
                {selected.room_reference && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Room: {selected.room_reference}</div>}
              </div>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => setSelected(null)}>×</button>
            </div>

            {/* Timeline */}
            <div style={{ marginBottom: '1.25rem', padding: '0.75rem', background: 'var(--bg-light)', borderRadius: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                {TIMELINE.map((step, i) => {
                  const current = timelineIndex(selected.status);
                  const done    = i < current;
                  const active  = i === current;
                  return (
                    <React.Fragment key={step.status}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: done || active ? 'var(--primary)' : '#d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                          {done ? '✓' : i + 1}
                        </div>
                        <div style={{ fontSize: '0.6rem', color: active ? 'var(--primary)' : 'var(--text-secondary)', textAlign: 'center', marginTop: '0.3rem', fontWeight: active ? 700 : 400, lineHeight: 1.2 }}>
                          {step.label}
                        </div>
                      </div>
                      {i < TIMELINE.length - 1 && (
                        <div style={{ flex: 1, height: '2px', background: i < current ? 'var(--primary)' : '#d1d5db', marginTop: '11px', marginBottom: 'auto' }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Details */}
            <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.8rem', marginBottom: '1rem' }}>
              {[
                ['Status',    <Badge value={selected.status} map={STATUS_COLOURS} />],
                ['Priority',  <Badge value={selected.priority} map={PRIORITY_COLOURS} />],
                ['Category',  selected.category],
                ['Reported',  fmtDate(selected.reported_date)],
                selected.contractor_name && ['Contractor', selected.contractor_name],
                selected.quote_amount && ['Quote', <span style={{ fontWeight: 700, color: '#166534' }}>{symChar}{parseFloat(selected.quote_amount).toFixed(2)}</span>],
                selected.actual_cost && ['Final Cost', <span style={{ fontWeight: 700 }}>{symChar}{parseFloat(selected.actual_cost).toFixed(2)}</span>],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                  <span>{value}</span>
                </div>
              ))}
              {selected.quote_notes && (
                <div style={{ background: 'var(--bg-light)', borderRadius: '0.375rem', padding: '0.5rem', fontStyle: 'italic', marginTop: '0.25rem' }}>
                  "{selected.quote_notes}"
                </div>
              )}
              {selected.description && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                  <div style={{ color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Description</div>
                  <div>{selected.description}</div>
                </div>
              )}
            </div>

            {actionError && <div className="alert alert-danger" style={{ marginBottom: '0.75rem', fontSize: '0.8rem' }}>{actionError}</div>}

            {/* Actions */}
            <div style={{ display: 'grid', gap: '0.75rem' }}>

              {/* Assign contractor */}
              {['new', 'assigned'].includes(selected.status) && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    {selected.status === 'assigned' ? 'Reassign Contractor' : 'Assign Contractor'}
                  </div>
                  <select className="form-control" style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}
                    value={assignId} onChange={e => setAssignId(e.target.value)}>
                    <option value="">Select contractor...</option>
                    {contractors.map(c => <option key={c.id} value={c.id}>{c.name}{c.trade ? ` — ${c.trade}` : ''}</option>)}
                  </select>
                  <button className="btn btn-primary" style={{ width: '100%', fontSize: '0.875rem' }}
                    onClick={handleAssign} disabled={actionLoading || !assignId}>
                    {actionLoading ? 'Assigning...' : '📋 Assign & Request Quote'}
                  </button>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.4rem', marginBottom: 0 }}>
                    Contractor will receive an email with a quote submission link.
                  </p>
                </div>
              )}

              {/* Quote received */}
              {selected.status === 'quote_received' && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.75rem' }}>Quote Received from {selected.contractor_name}</div>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.75rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#166534' }}>{symChar}{parseFloat(selected.quote_amount || 0).toFixed(2)}</div>
                    {selected.quote_notes && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem', fontStyle: 'italic' }}>"{selected.quote_notes}"</div>}
                  </div>
                  <button className="btn btn-primary" style={{ width: '100%', fontSize: '0.875rem', marginBottom: '0.75rem' }}
                    onClick={handleAcceptQuote} disabled={actionLoading}>✅ Accept Quote</button>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Reason for declining (sent to contractor)</div>
                    <textarea value={rejectComment} onChange={e => setRejectComment(e.target.value)}
                      rows={2} placeholder="e.g. Quote too high, please resubmit..."
                      style={{ width: '100%', fontSize: '0.8rem', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.375rem', resize: 'vertical', marginBottom: '0.5rem', boxSizing: 'border-box' }} />
                    <button className="btn btn-secondary" style={{ width: '100%', fontSize: '0.875rem', color: 'var(--danger)' }}
                      onClick={handleRejectQuote} disabled={actionLoading}>❌ Reject Quote</button>
                  </div>
                </div>
              )}

              {/* Status updates */}
              {selected.status === 'in_progress' && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                  <button className="btn btn-secondary" style={{ width: '100%', fontSize: '0.875rem' }}
                    onClick={() => handleUpdateStatus('awaiting_parts')} disabled={actionLoading}>
                    ⏳ Mark Awaiting Parts
                  </button>
                </div>
              )}
              {selected.status === 'awaiting_parts' && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                  <button className="btn btn-secondary" style={{ width: '100%', fontSize: '0.875rem' }}
                    onClick={() => handleUpdateStatus('in_progress')} disabled={actionLoading}>
                    🔧 Mark Back In Progress
                  </button>
                </div>
              )}

              {/* Complete */}
              {['in_progress', 'awaiting_parts', 'assigned'].includes(selected.status) && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem' }}>Mark as Completed</div>
                  <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.75rem' }}>Final Cost ({symChar})</label>
                    <input type="number" step="0.01" value={completeForm.actual_cost}
                      onChange={e => setCompleteForm(p => ({ ...p, actual_cost: e.target.value }))}
                      placeholder="0.00" style={{ fontSize: '0.875rem' }} />
                  </div>
                  {completeForm.actual_cost && parseFloat(completeForm.actual_cost) > 0 && (
                    <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.8rem', cursor:'pointer', marginBottom:'0.5rem' }}>
                      <input type="checkbox" checked={completeForm.create_expense}
                        onChange={e => setCompleteForm(p => ({ ...p, create_expense: e.target.checked }))} />
                      Auto-create expense ({symChar}{parseFloat(completeForm.actual_cost||0).toFixed(2)} — Maintenance & Repairs)
                    </label>
                  )}
                  <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.75rem' }}>Completion Notes</label>
                    <textarea value={completeForm.notes}
                      onChange={e => setCompleteForm(p => ({ ...p, notes: e.target.value }))}
                      rows={2} placeholder="Notes on completed work..." style={{ width: '100%', fontSize: '0.875rem', resize: 'vertical' }} />
                  </div>
                  <button className="btn btn-primary" style={{ width: '100%', fontSize: '0.875rem' }}
                    onClick={handleComplete} disabled={actionLoading}>
                    {actionLoading ? 'Saving...' : '✅ Mark Complete'}
                  </button>
                </div>
              )}

              {/* Close */}
              {selected.status === 'completed' && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem', display: 'grid', gap: '0.5rem' }}>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', color: '#166534', fontWeight: 600 }}>
                    ✅ Completed {fmtDate(selected.completed_date)}
                    {selected.actual_cost && ` — ${symChar}${parseFloat(selected.actual_cost).toFixed(2)}`}
                  </div>
                  <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}
                    onClick={() => handleUpdateStatus('closed')} disabled={actionLoading}>
                    🔒 Close Request
                  </button>
                </div>
              )}

              {/* Cancel */}
              {!['completed', 'closed', 'cancelled'].includes(selected.status) && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
                  <button className="btn btn-secondary" style={{ width: '100%', fontSize: '0.8rem', color: 'var(--danger)' }}
                    onClick={handleCancel} disabled={actionLoading}>
                    Cancel Request
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" style={{ maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">+ New Service Request</span>
              <button className="modal-close" onClick={() => setShowAdd(false)}>×</button>
            </div>

            {formError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{formError}</div>}

            <form onSubmit={handleAdd}>
              <div className="form-group">
                <label>Property *</label>
                <select value={form.property_id} onChange={e => set('property_id', e.target.value)} required>
                  <option value="">Select property...</option>
                  {properties.filter(p => !p.archived).map(p => (
                    <option key={p.id} value={p.id}>{p.address}</option>
                  ))}
                </select>
              </div>

              {isHMO && (
                <div className="form-group">
                  <label>Room Reference <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>(optional)</span></label>
                  <input value={form.room_reference} onChange={e => set('room_reference', e.target.value)}
                    placeholder="e.g. Room 1, Master Bedroom" />
                </div>
              )}

              <div className="form-group">
                <label>Title *</label>
                <input value={form.title} onChange={e => set('title', e.target.value)}
                  placeholder="e.g. Boiler not working" required />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)}
                  rows={3} placeholder="Describe the issue..." style={{ width: '100%', resize: 'vertical' }} />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Category</label>
                  <select value={form.category} onChange={e => set('category', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_COLOURS[p]?.label || p}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date Reported</label>
                  <input type="date" value={form.reported_date} onChange={e => set('reported_date', e.target.value)} />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                  rows={2} placeholder="Any additional notes..." style={{ width: '100%', resize: 'vertical' }} />
              </div>

              <div className="form-group">
                <label>Assign Contractor <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>(optional — can assign later)</span></label>
                <select value={form.contractor_id} onChange={e => set('contractor_id', e.target.value)}>
                  <option value="">Select contractor...</option>
                  {contractors.map(c => <option key={c.id} value={c.id}>{c.name}{c.trade ? ` — ${c.trade}` : ''}</option>)}
                </select>
                {form.contractor_id && (
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    ✉️ Contractor will receive a quote request email immediately on submission
                  </small>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : '✅ Create Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
